import { useQuery } from '@tanstack/react-query';
import type { SocialPostPreviewCardMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';
import type { TaskDecisionPrompt } from '@wix/ai-scheduling-assistant-common-lib/utils';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import type { SentOutreachSummary } from './outreach-rows';

export interface TaskStep {
  title: string;
  completed: boolean;
}

/** One note the user wrote on a task — a `note` entry of its change log. */
export interface TaskNote {
  id: string;
  content: string;
  /** ISO, or '' when the entry carried no date. */
  date: string;
}

export interface TaskDetail {
  id: string;
  title: string;
  subtitle: string;
  summary: string;
  /** The task's SPEC — what the agent works from. This is the field an edit writes, NOT `summary`,
   *  which is model-authored prose the harness deliberately never overwrites. */
  brief?: string;
  /** The user's own notes on the task, oldest first. Absent on a payload from before they existed. */
  notes?: TaskNote[];
  changeLog?: { date: string; label: string }[];
  currentAction: string;
  status: string;
  agentId: string;
  steps: TaskStep[];
  /** Full VFS paths of the files the task produced (`display.artifacts`, e.g.
   *  `workspaces/<slug>/artifacts/<name>`). The detail's Files section resolves + opens each. */
  artifacts: string[];
  workspaceId: string;
  /** The Maestro chat this task was born in — what "Resolve with Maestro" continues, so the agent already
   *  has the task's history. Empty when the origin conversation is not a chat the user can be dropped into
   *  (a routine's PULSE thread, a goal tool's own conversation) — the CTA seeds a new chat then. */
  resolveConversationId?: string;
  date: string | null;
  /** When the task was created — shown on the detail so you can see how long it's been running. */
  createdDate: string | null;
  /** This task tracks a dashboard BUILD, which runs service-side with no agent conversation behind it
   *  (see the harness's `dashboard-build-task.ts`). The activity section reads it to say so, rather than
   *  promising a transcript that will never arrive. */
  isDashboardBuild?: boolean;
  /** The build's marker: the dashboard it targets and, once the run ended, what it produced (`lib/dashboard-build.ts`). */
  dashboardBuild?: import('./dashboard-build').DashboardBuildMarker;
  /** The goal whose plan laid this task out, and that goal's title — the detail names its origin and links
   *  to it, exactly as the list row does. Empty when the task serves no goal, or its goal could not be read. */
  goalId?: string;
  goalTitle?: string;
  /** The worker agent's own conversation. Read here for ONE reason: it is the durable "an agent has been
   *  put on this" record, so `isNotStartedTask` can tell a task laid out and never begun from one that is
   *  genuinely under way. Not a transcript link — that is `AgentConversationPanel`'s own read. */
  agentConversationId?: string;
  /** The instant the agent may not start before, ISO. Same derived-`scheduled` rule as the list row. */
  notBefore?: string;
  /** The tasks this one waits for — see `WebTask.blockedByTasks`. The detail says "Starts after …" from it. */
  blockedByTasks?: import('./task-schedule').TaskBlockerRef[];
  /** What this task's agent SENT on the user's behalf — recipient, the whole correspondence, and whether
   *  they wrote back. Read by the detail route itself (`sentOutreachesForTask`) so the section is present
   *  however the detail was reached; a task opened from a goal's plan has no loaded task list to read it
   *  out of, which is how a task that had messaged three people showed no message at all. */
  sentOutreaches?: SentOutreachSummary[];
  /** The open question(s) this task is held on, and the tri-state's third value. Same shape and same rule
   *  as the list row (`readTaskDecisionPrompts`): absent+absent means nothing to answer, a non-empty array
   *  means a question is in hand, and `decisionPromptsUnavailable: true` alone means the read failed —
   *  never render the third as the first. */
  decisionPrompts?: TaskDecisionPrompt[];
  decisionPromptsUnavailable?: boolean;
  socialPostPreviewCard?: SocialPostPreviewCardMetadata;
  /**
   * Whether an agent loop is executing on this task right now. **Absent = not computed, never "no".**
   *
   * The single-task name for the verdict the LIST stamps as `runInFlight` — the pair `isInMotion` reads,
   * and the reason it reads both. Before the detail route ran the run-state join, the drill-in could not
   * answer "is anything happening on this?" while the row the reader opened it from could.
   */
  isLive?: boolean;
}

export type TaskDetailState = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; task: TaskDetail };

// Fetches one task's normalized detail (/api/task/:id). Shared by the task
// detail page and the Symphony task-details side panel. Cached via react-query
// so re-opening a task the user just viewed renders instantly (no loading
// shimmer) and refetches in the background once stale.
/**
 * The query itself, so a caller with no detail on screen can still ASK for one.
 *
 * The list payload deliberately carries no `resolveConversationId` — resolving it costs a conversation
 * read per task, which the list would pay 30 days over. So a row-level "Resolve with Maestro" has to
 * fetch the detail before it can continue the right chat, via `queryClient.fetchQuery`; it hits the same
 * cache the detail panel fills, so a row whose task was just open resolves with no request at all.
 */
export function taskDetailQuery(id: string) {
  return {
    queryKey: ['task-detail', id],
    queryFn: async (): Promise<TaskDetail> => {
      const res = await apiFetch(`${API_BASE}/api/task/${encodeURIComponent(id)}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as { task?: TaskDetail };
      if (!body.task) throw new Error('no task');
      return body.task;
    },
  };
}

export function useTaskDetail(id: string): TaskDetailState {
  const { data, isError } = useQuery({
    ...taskDetailQuery(id),
    enabled: !!id,
  });

  if (data) return { kind: 'ready', task: data };
  if (isError) return { kind: 'error' };
  return { kind: 'loading' };
}
